import React, { useEffect, useRef, useState } from "react";
import { Grid, GridItem } from "@chakra-ui/react";
import { ModelInfo, ColumnInfo } from "build-ts/metadata";
import { ConfigurationContextProvider, useConfigurationContext } from "context/ConfigurationContext";
import { useUIContext } from "context/UIContext";
import { useLocation, useMatch } from "react-router-dom";
import ConfigPageMainPanel from "modules/configuration/components/ConfigPageMainPanel";
import ConfigurationNavBar from "modules/configuration/components/ConfigurationNavBar";

// ---------------------------------------------------------------------------
// ConfigurationPage
// ---------------------------------------------------------------------------

const ENTITY_VIEWS = ["metrics", "benchmarks", "measures", "attributes", "time"];

const ConfigurationPage: React.FC = () => (
  <ConfigurationContextProvider>
    <ConfigurationPageContent />
  </ConfigurationContextProvider>
);

const ConfigurationPageContent: React.FC = () => {
  const location = useLocation();
  const match = useMatch("/mkg/models/:id");
  const tsMatch = useMatch("/mkg/thoughtspot/:tsId");
  const metricsIdMatch = useMatch("/mkg/metrics/:id");
  const measuresIdMatch = useMatch("/mkg/measures/:id");
  const attributesIdMatch = useMatch("/mkg/attributes/:id");
  const timeframesIdMatch = useMatch("/mkg/timeframes/:id");
  const benchmarksIdMatch = useMatch("/mkg/benchmarks/:id");

  const { setIsNavbarCollapsed } = useUIContext();
  const { fetchModelList, fetchKgColumns } = useConfigurationContext();

  const [activeButton, setActiveButton] = useState<string>("ViewMKG");
  const [modelInfo, setModelInfo] = useState<ModelInfo | undefined>(undefined);
  const [addedColumns, setAddedColumns] = useState<ColumnInfo[]>([]);
  const [removedColumns, setRemovedColumns] = useState<ColumnInfo[]>([]);
  const [shouldReloadKG, setShouldReloadKG] = useState<boolean>(false);

  // Keep a ref so the location effect can read activeButton without re-running
  const activeButtonRef = useRef(activeButton);
  useEffect(() => {
    activeButtonRef.current = activeButton;
  }, [activeButton]);

  // Fetch model list and kg columns when the component mounts
  useEffect(() => {
    fetchModelList();
    fetchKgColumns();
  }, []);

  // URL-driven navigation — preserve entity views when transitioning to /mkg
  useEffect(() => {
    if (location.pathname === "/mkg") {
      setIsNavbarCollapsed(true);
      if (!ENTITY_VIEWS.includes(activeButtonRef.current)) {
        setActiveButton("ViewMKG");
      }
    } else if (location.pathname === "/mkg/metrics" || metricsIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("metrics");
    } else if (location.pathname === "/mkg/measures" || measuresIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("measures");
    } else if (location.pathname === "/mkg/attributes" || attributesIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("attributes");
    } else if (location.pathname === "/mkg/timeframes" || timeframesIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("time");
    } else if (location.pathname === "/mkg/benchmarks" || benchmarksIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("benchmarks");
    } else if (location.pathname === "/mkg/models") {
      setIsNavbarCollapsed(true);
      setActiveButton("modelList");
    } else if (location.pathname === "/mkg/models/new") {
      setIsNavbarCollapsed(true);
      setActiveButton("createModel");
    } else if (match) {
      setIsNavbarCollapsed(true);
      setActiveButton("updateModel");
    } else if (location.pathname === "/mkg/thoughtspot" || tsMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("thoughtspotEmbed");
    } else if (location.pathname === "/mkg/memory") {
      setIsNavbarCollapsed(true);
      setActiveButton("memory");
    }
  }, [
    location.pathname,
    match,
    tsMatch,
    metricsIdMatch,
    measuresIdMatch,
    attributesIdMatch,
    timeframesIdMatch,
    benchmarksIdMatch,
  ]);

  const handleReloadKnowledgeGraph = () => {
    setShouldReloadKG(true);
    setTimeout(() => setShouldReloadKG(false), 100);
  };

  return (
    <Grid templateAreas={'"sidebar main"'} gridTemplateRows="1fr" gridTemplateColumns="auto 1fr" height="100%" p={0}>
      {/* ---------------------------------------------------------------- */}
      {/* Sidebar                                                           */}
      {/* ---------------------------------------------------------------- */}
      <GridItem area="sidebar" bg="gray.50" borderRightWidth="1px" borderColor="gray.200">
        <ConfigurationNavBar activeButton={activeButton} setActiveButton={setActiveButton} />
      </GridItem>

      {/* ---------------------------------------------------------------- */}
      {/* Main panel                                                        */}
      {/* ---------------------------------------------------------------- */}
      <GridItem area="main" height="100%" minH={0} overflow="hidden">
        <ConfigPageMainPanel
          activeButton={activeButton}
          shouldReloadKG={shouldReloadKG}
          modelInfo={modelInfo}
          setModelInfo={setModelInfo}
          addedColumns={addedColumns}
          removedColumns={removedColumns}
          setAddedColumns={setAddedColumns}
          setRemovedColumns={setRemovedColumns}
          fetchModelList={fetchModelList}
          handleReloadKnowledgeGraph={handleReloadKnowledgeGraph}
          tsMatchTsId={tsMatch?.params?.tsId}
        />
      </GridItem>
    </Grid>
  );
};

export default ConfigurationPage;
